import * as _ from "lodash";
import { Action, ActionCreator, AnyAction, bindActionCreators, Dispatch } from "redux";
import { BatchAction, batchActions } from "redux-batched-actions";
import { ThunkAction } from "redux-thunk";
import {
	ATTRIBUTE_PLACEHOLDER_DEFAULT_HEIGHT,
	DEFAULT_IMAGE_PLACEHOLDER_HEIGHT,
	DEFAULT_IMAGE_PLACEHOLDER_WIDTH,
	PERCENTAGE_HUNDRED,
	SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES,
	VERTICALALIGN_KEY,
} from "../../Constants";
import { IExtents } from "../../widgets/interfaces/model/dataStructures/index";
import { IOffset } from "../../widgets/interfaces/model/dataStructures/Offset";
import { CanvasMode } from "../../widgets/interfaces/model/enumerations/CanvasMode";
import { FillTypes } from "../../widgets/interfaces/model/enumerations/FillTypes";
import { GeometryTypes } from "../../widgets/interfaces/model/enumerations/GeometryTypes";
import {
	AutosizeMode,
	CompoundLine,
	DashPresets,
	IAdjustHandlerDirection,
	IPageObjectTypes,
	IReferenceDimension,
	Keys,
	LineCap,
	PathOperationTypes,
	ShapeType,
} from "../../widgets/interfaces/model/enumerations/index";
import { PageObjectScalePointType } from "../../widgets/interfaces/model/enumerations/PageObjectScalePoints";
import { ICustomGeometry, IHandlePoint, IPenGeometry } from "../../widgets/interfaces/model/geometry/CustomGeometry";
import { IShapeGuide } from "../../widgets/interfaces/model/geometry/ShapeGuide";
import { IShapePath } from "../../widgets/interfaces/model/geometry/ShapePath";
import { IXYAdjustHandle } from "../../widgets/interfaces/model/geometry/XYAdjustHandle";
import { ICanvasEditorState, IPage } from "../../widgets/interfaces/model/index";
import { IPageMeta } from "../../widgets/interfaces/model/PageMeta";
import { IConnectionShape } from "../../widgets/interfaces/model/PageObjects/ConnectionShape";
import { IGroupShape } from "../../widgets/interfaces/model/PageObjects/index";
import { IShape } from "../../widgets/interfaces/model/PageObjects/Shape";
import { IPlaceholderFillData } from "../../widgets/interfaces/model/PlaceholderFillData";
import { PageObjectType } from "../../widgets/interfaces/model/Types";
import { IPosition } from "../../widgets/meta/Drag/CsiDragController";
import { IDragResults } from "../../widgets/meta/Drag/index";
import { getCanvasMode } from "../../widgets/selectors/canvasMode";
import {
	getSelectedPage, getSelectedPageObjects,
	stateConstants
} from "../../widgets/selectors/index";
import {
	buildShapePointList,
	generatePenOperationList,
	generateShapePathList,
	getMigratedShapeIds,
	getModifiedCurveDelta,
} from "../../widgets/util";
import { PageActionConstants } from "../reducers/CanvasPage";
import {
	CanvasEditorConstants,
	ClipboardActionConstants,
	GuideActionConstants,
	IAddImageToPlaceholderAction,
	IAddPageAction,
	IAddPageObjectAction,
	IAlignObjectAction,
	IChangeOrderAction,
	IDeleteObjectsAction,
	IDeletePageAction,
	IDeselectAllObjectAction,
	IDistributeObjectAction,
	IFlipObjectAction,
	IFormatPainterAction,
	IGroupAction,
	ILockObjectAction,
	IMoveObjectsAction,
	IObjectFlip,
	IPasteObjectAction,
	IRemoveMigratedExtents,
	IResetClipboardAction,
	IResetPlaceholderExtendsAction,
	IRotateObjectAction,
	IScaleImageToFrameAction,
	IScaleObjectAction,
	IScaleObjectExtendsAction,
	ISelectObjectAction,
	ISelectObjectsByIndexAction,
	ISelectPageAction,
	ISetAllAspectRatiosAction,
	ISetAspectRatioAction,
	ISetCanvasModeAction,
	ISetCurvePointsAction,
	ISetFormatPainterAction,
	ISetGeometryAction,
	ISetGuidesAction,
	ISetHandlePointsAction,
	ISetObjectExtendsAction,
	ISetOffsetsAction,
	ISetPageAction,
	ISetPageMetaAction,
	ISetSelectedCurvePointAction,
	ISetShapeTextContentAction,
	ISetShapeTextStyle,
	ISortPagesAction,
	ITranslateAction,
	IUnGroupAction,
	IUpdateClipboardAction,
} from "../reducers/index";
import {
	IAddAttributeToPlaceholderAction,
	IAddImageRequest,
	IAddPageObjectsAction,
	IEditPlaceholderAction,
	IRemoveKeysAction,
} from "../reducers/PageOperations";
import { SelectionConstants } from "../reducers/Selection";
import {
	deepFindOutermostGroup,
	deepGenerateNewUUIDs,
	filterSelectedObjects, filterUnLockedObjects, generateUUID,
	getAdjustHandlerPercentage,
	getAttributePlaceholderObject,
	getGuidesPoints,
	getImageExtents,
	getImageObject,
	getImagePlaceholderObject,
	getIndexedChildren,
	isCornerScalePoint,
	manipulateDimensions,
	mapGroupedPointToShapeMatrix,
	moveObjectsToFront,
	setFroalaTextStyle,
} from "../reducers/util";
import { supportsArrow } from "../SelectionHelper";

/**
 * Add page action creator.
 * @param page {IPage} Page to be added.
 * @returns {IAddPageAction} Add page action.
 */
export const addPage: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (page: IPage) =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage, selectedPageObjects } = state.selection.present;

		// this page will be inserted next to the selected page.
		const indexOfNewPage = (selectedPage || 0) + 1;

		// Select object by index action called to keep track of selection in undo/redo stack
		const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			transient: !state.canvasEditor.isEditEnabled,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};
		const addPageAction: IAddPageAction = {
			index: indexOfNewPage,
			page,
			transient: !state.canvasEditor.isEditEnabled,
			type: PageActionConstants.ADD_PAGE,
		};
		const selectPageAction: ISelectPageAction = {
			selectedPage: indexOfNewPage,
			transient: !state.canvasEditor.isEditEnabled,
			type: SelectionConstants.SELECT_PAGE,
		};
		return dispatch(batchActions([addPageAction, selectObjectsByIndexAction, selectPageAction]));
	};

/**
 * Set page meta data action.
 * @param meta {IPageMeta} PageMeta to be set.
 * @returns {ISetPageMetaAction} Set page meta action.
 */
export const setPageMeta:
	ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (meta: IPageMeta) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPage } = state.selection.present;

			if (!selectedPage) {
				throw "ERROR: Page must be selected to perform add object action.";
			}
			const { selectedPageObjects } = state.selection.present;
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				transient: false,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			const setPageMetaDataAction: ISetPageMetaAction = {
				meta,
				transient: false,
				selectedPage,
				type: PageActionConstants.SET_PAGE_META,
			};
			return dispatch(batchActions([setPageMetaDataAction, selectObjectsByIndexAction]));
		};

/**
 * Delete page action creator.
 * @param page {IPage} Page to be added.
 * @returns {BatchAction}
 */
export const deletePage: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (page: number) =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const pages = state.pages.present;
		const { selectedPageObjects, selectedPage } = state.selection.present;
		const actions: Action[] = [];
		const preserveObjectSelection = selectedPage !== page;
		// Select object by index action called to keep track of selection in undo/redo stack
		actions.push({
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		} as ISelectObjectsByIndexAction);
		actions.push({
			type: PageActionConstants.DELETE_PAGE,
			page,
		} as IDeletePageAction);

		// we need to update selected page index
		if (selectedPage && selectedPage > page) {
			actions.push({
				preserveObjectSelection,
				selectedPage: selectedPage - 1,
				transient: !state.canvasEditor.isEditEnabled,
				type: SelectionConstants.SELECT_PAGE,
			} as ISelectPageAction);
		} else if (selectedPage && selectedPage === page) {
			// if current page is deleted, we need select next or previous page accordingly.
			// previous page will be selected if there is no next page.
			if (selectedPage === pages.length) {
				// this is last page.
				actions.push({
					preserveObjectSelection,
					selectedPage: selectedPage - 1,
					transient: !state.canvasEditor.isEditEnabled,
					type: SelectionConstants.SELECT_PAGE,
				} as ISelectPageAction);
			}
			// else selectedPage is not last page
			// we don't have to handle this case,
			// since next page will have same index as that of current page after deletion.
		}
		return dispatch(batchActions(actions));
	};

/**
 * Delete objects action creator.
 * @returns {BatchAction | undefined} Delete objects action & Deselect all objects.
 */
export const deleteObjects: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction>> =
	() =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			let { groupSelectionIndices } = state.selection.present;

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform delete objects action.";
			}

			const page = getSelectedPage(state) as IPage;
			if (selectedPageObjects.length === 1 && page.pageObjects[selectedPageObjects[0]].spPr.locked) {
				return;
			}

			const selectedObjects = page.pageObjects.filter(filterSelectedObjects(selectedPageObjects));
			const unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
			const unlockedObjectIndices = unlockedSelectedObjects.map((unlockedPO) => {
				return _.findIndex(page.pageObjects, (po) => {
					return po.shapeId === unlockedPO.shapeId;
				});
			});
			if (!unlockedObjectIndices || !unlockedObjectIndices.length) {
				// in case no unlocked objects then do nothing.
				return;
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const deselectAllObjects: IDeselectAllObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};
			// clone originally selected objects
			const placeholderObject = _.cloneDeep(selectedObjects[0]) as IGroupShape;
			if (placeholderObject.childObjects && groupSelectionIndices &&
				groupSelectionIndices.length === placeholderObject.childObjects.length) {
				groupSelectionIndices = [];
			}

			// only delete subgroup element
			const deleteObjectsAction: IDeleteObjectsAction = {
				groupSelectionIndices,
				selectedPageObjects: unlockedObjectIndices,
				selectedPage,
				type: PageActionConstants.DELETE_OBJECTS,
			};
			const actions = [selectObjectsByIndexAction, deleteObjectsAction, deselectAllObjects];
			// if a placeholder object is selected and it is part of a placeholder group, reposition elements
			if (
				placeholderObject.isAttributePlaceholder &&
				groupSelectionIndices &&
				placeholderObject.childObjects.length > 1 &&
				groupSelectionIndices.length
			) {
				const resetPlaceholderExtendsAction: IResetPlaceholderExtendsAction = {
					type: PageActionConstants.RESET_PLACEHOLDER_EXTENDS,
					groupSelectionIndices,
					selectedPage,
					selectedPageObjects,
				};

				// remove selected subgroup element
				_.pullAt(placeholderObject.childObjects, groupSelectionIndices);
				actions.push(resetPlaceholderExtendsAction);
			}
			return dispatch(batchActions(actions));
		};

/**
 * Remove impossible paths action creator
 * @returns {BatchAction | undefined} Delete objects action & Deselect all objects.
 */
export const removeImpossiblePaths: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction>> =
	(keys: string[]) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { selectedPage } = state.selection.present;

			const deleteKeysAction: IRemoveKeysAction = {
				keys,
				selectedPage,
				type: PageActionConstants.DELETE_KEYS,
			};
			const deselectAllObjects: IDeselectAllObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};
			return dispatch(batchActions([deleteKeysAction, deselectAllObjects]));
		};

/**
 * Delete shape by shapeId action interface
 * @param shapeId {string}
 * @param transient {boolean}
 * @return {IDeleteObjectByIndexAction}
 */
export const deleteShapeById: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(shapeId: string, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			const page = getSelectedPage(state) as IPage;
			// unlock selected object indices
			const shape = _.find(page.pageObjects, (pageObject) => {
				return shapeId === pageObject.shapeId;
			});
			const isLockedShape: boolean | undefined = shape && shape.spPr.locked;
			// if shape is selected shape update the selection
			const shapeIndex = _.findIndex(page.pageObjects, (pageObject) => {
				return pageObject.shapeId === shapeId;
			});
			const isShapeSelected = selectedPageObjects.indexOf(shapeIndex) !== -1;
			// new shape selection
			let toBeSelectedShapes: number[] = [...selectedPageObjects];
			// Throw error message if there is no page selected
			if (!selectedPage || isLockedShape) {
				throw "ERROR: Page should be selected and shape with shapeId is unlocked perform delete object action.";
			}
			const actions: AnyAction[] = [];
			actions.push({
				transient,
				type: PageActionConstants.DELETE_SHAPE_BY_ID,
				selectedPageObjects,
				selectedPage,
				shapeId,
			});
			actions.push({
				transient,
				type: SelectionConstants.DELETE_ASPECT_RATIO,
				shapeId,
			});
			if (isShapeSelected) {
				toBeSelectedShapes.splice(selectedPageObjects.indexOf(shapeIndex), 1);
				// shift the shape selection indices
				toBeSelectedShapes = _.map(toBeSelectedShapes, (index) => {
					return shapeIndex > index ? index : index - 1;
				});
				actions.push({
					selectedPageObjects: toBeSelectedShapes,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				});
			}
			return dispatch(batchActions(actions));
		};

/**
 * Set pages action creator.
 * Note: All existing pages will be removed and new pages will be added.
 * @param pages  {IPage[]} Pages to be set.
 * @returns {ISetPageAction} Set page action.
 */
export const setPages: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(pages: IPage[], preserveSelection?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const actions: AnyAction[] = [];
			// assumption that we always have only one page in state.
			let lastOperationCount = -1;
			if (state.pages.present && state.pages.present.length) {
				lastOperationCount = state.pages.present[0].contentChanges;
			}
			lastOperationCount = lastOperationCount ? lastOperationCount : -1;
			const newPages = _.map(pages, (page) => {
				return { ...page, contentChanges: lastOperationCount };
			});
			// we want setPages to be first action in batch.
			actions.push({
				pages: newPages,
				transient: !state.canvasEditor.isEditEnabled,
				type: PageActionConstants.SET_PAGES,
			} as ISetPageAction);

			// Deselect all objects action
			const deselectObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			} as IDeselectAllObjectAction;
			if (!preserveSelection) {
				actions.push(deselectObjectAction);
			} else if (state.canvasEditor.mode === CanvasMode.EDIT_CURVE || state.canvasEditor.mode === CanvasMode.EDIT_PEN) {
				const page = pages[state.selection.present.selectedPage as number - 1];
				const objectIndex = state.selection.present.selectedPageObjects[0];
				const selectedObject = page.pageObjects[objectIndex];
				if (selectedObject && !supportsArrow([selectedObject])) {
					actions.push(deselectObjectAction);
				} else if (!selectedObject) {
					// Set canvas mode action
					actions.push({
						mode: CanvasMode.SELECT,
						transient: true,
						type: CanvasEditorConstants.SET_CANVAS_MODE,
					} as ISetCanvasModeAction);
					actions.push(deselectObjectAction);
				}
			}
			// Reset format painter flag on page change
			if (state.canvasEditor.isFormatPainterActive) {
				actions.push({
					isFormatPainter: false,
					transient: true,
					type: CanvasEditorConstants.SET_FORMAT_PAINTER,
				} as ISetFormatPainterAction);
			}
			return dispatch(batchActions(actions));
		};

/**
 * Set objects fill action creator.
 * If page object selected then set fill for the object else create action to set shape default properties.
 * @param color {string} object fill color
 * @return {AnyAction}
 */
export const setObjectsFill: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(color: string) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setFillAction = {
					type: PageActionConstants.SET_FILL,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					color,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setFillAction]));
			} else {
				const shapeProperties = {
					spPr: {
						fill: {
							color,
						},
					},
				};
				return dispatch({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
		};

/**
 * Set objects shape action creator.
 * If page object selected then set shape for the object else create action to set shape default properties.
 * @param string {string} object shape
 * @return {AnyAction}
 */
export const setObjectsShape: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(presetShape: string) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setShapeAction = {
					newShapeType: presetShape,
					transient: false,
					type: PageActionConstants.CHANGE_SHAPE_TYPE,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setShapeAction]));
			} else {
				const shapeProperties = {
					spPr: {
						geometry: {
							presetShape,
						},
					},
				};
				return dispatch({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
		};

/**
 * Set line color action creator.
 * If page object selected then set line color for the object else create action to set shape default properties.
 * @param color {string} object line color
 * @return {AnyAction}
 */
export const setLineColor: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(color: string) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setLineColorAction = {
					type: PageActionConstants.SET_LINE_COLOR,
					groupSelectionIndices,
					selectedPageObjects,
					selectedPage,
					color,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setLineColorAction]));
			} else {
				const shapeProperties = {
					spPr: {
						ln: {
							fill: {
								color,
							},
						},
					},
				};
				return dispatch({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
		};

/**
 * Translate object action creator.
 * @param drag { IDragResults } translate by dx
 * @return { BatchAction | undefined | ITranslateAction }
 */
export const translateObjects: ActionCreator<ThunkAction<BatchAction | ITranslateAction | undefined,
	ICanvasEditorState, void, BatchAction | ITranslateAction>> = (drag: IDragResults, transient?: boolean, isEscape?: boolean) =>
		(dispatch: Dispatch<BatchAction | ITranslateAction>, getState: () => ICanvasEditorState):
			BatchAction | ITranslateAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			let actions: any[] = [];

			// If canvas text edit mode is on, then do not translate object
			if (state.canvasEditor.mode === CanvasMode.EDIT_TEXT || !state.canvasEditor.isEditEnabled) {
				return;
			}
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform translate action.";
			}

			// IF canvas is in EDIT_PICTURE then image translation will happen and not that of object
			if (state.canvasEditor.mode === CanvasMode.EDIT_PICTURE) {
				return dispatch({
					dx: drag.delta.x,
					dy: drag.delta.y,
					type: PageActionConstants.TRANSLATE_CROPPED_IMAGE,
					groupSelectionIndices,
					selectedPageObjects,
					selectedPage,
					transient,
				});
			}
			let dx = drag.delta.x;
			let dy = drag.delta.y;
			if (!transient) {
				// Translate action will be called as last action which is used as
				// dummy action to notify the undo the translate action(without
				// transient: true flag). Select object by index action called to
				// keep track of selection in undo/redo stack.
				actions = [{
					transient,
					type: PageActionConstants.TRANSLATE_OBJECTS,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					dx,
					dy,
				}, {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				}];

				// Also add guide related actions.
				actions = actions.concat(createGuidelineActions(transient));
				return dispatch(batchActions(actions));
			}

			// Get primary selected object
			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: No page selected to perform translate object operation";
			}
			const { pageObjects } = page;
			const selectedObject = pageObjects.filter((pageObject, index) => {
				return selectedPageObjects.indexOf(index) !== -1;
			})[0];

			// Unselected page objects
			const unselectedObjects: PageObjectType[] = pageObjects.filter((pageObject, index) => {
				return selectedPageObjects.indexOf(index) === -1;
			});

			// getGuidesPoints will return matched guides
			const guidesObj = getGuidesPoints(state, selectedObject, unselectedObjects, dx, dy);
			const isSnapToGuide = state.canvasEditor.isSnapToGuide && !selectedObject.spPr.locked;

			if (isSnapToGuide && !isEscape) {
				if (guidesObj.x) {
					dx = guidesObj.dx;
				}
				if (guidesObj.y) {
					dy = guidesObj.dy;
				}
			}

			// Add translate and guide related actions.
			actions = [{
				transient: true,
				type: PageActionConstants.TRANSLATE_OBJECTS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				dx,
				dy,
			}]
				.concat(createGuidelineActions(transient, isEscape, isSnapToGuide, guidesObj));

			return dispatch(batchActions(actions));
		};

/**
 * Move object action creator.
 * @param keyCode {number}
 * @param enableFastMode {boolean} flag to indicate fast mode enabled
 * @return { IMoveObjectsAction }
 */
export const moveObjects: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction>> =
	(keyCode: number, enableFastMode: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error messgae if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform translate action.";
			}
			let dx: number = 0;
			let dy: number = 0;
			const fineInc: number = 1;
			const gridInc = state.canvasEditor.gridInc;
			switch (keyCode) {
				case Keys.UP:
					dy = enableFastMode ? (-1 * gridInc) : -fineInc;
					break;
				case Keys.RIGHT:
					dx = enableFastMode ? gridInc : fineInc;
					break;
				case Keys.DOWN:
					dy = enableFastMode ? gridInc : fineInc;
					break;
				case Keys.LEFT:
					dx = enableFastMode ? (-1 * gridInc) : -fineInc;
					break;
				default:
					break;
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const moveObjectAction: IMoveObjectsAction = {
				type: PageActionConstants.TRANSLATE_OBJECTS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				dx,
				dy,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, moveObjectAction]));
		};

/**
 * Paste object action creator.
 * @return {BatchAction}
 */
export const paste: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	() =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			const { clipboard, clearOnPaste } = state.clipboard;
			const { pasteObjectOffset } = state.canvasEditor;
			if (!clipboard.length) {
				throw "ERROR: There is no object copied to perform paste action.";
			}
			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform paste action.";
			}
			const objectCounts = page.pageObjects.length;
			const selectedObjectCounts = clipboard.length;
			const toBeSelectedObjects: number[] = [];
			let actions = [];
			// Deselect all objects action
			const deselectAction: IDeselectAllObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};

			// Create new selection array
			for (let i = 0; i < selectedObjectCounts; i++) {
				toBeSelectedObjects.push(objectCounts + i);
			}
			// Select all objects action
			const selectObjectsAction: ISelectObjectsByIndexAction = {
				selectedPageObjects: toBeSelectedObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			// Paste object action
			const pasteAction: IPasteObjectAction = {
				type: PageActionConstants.PASTE,
				selectedPageObjects,
				selectedPage,
				clipboard,
			};

			// Update Clipboard for next paste operation
			const updateClipboardAction: IUpdateClipboardAction = {
				type: ClipboardActionConstants.UPDATE_CLIPBOARD,
				pasteObjectOffset,
			};
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			actions = [selectObjectsByIndexAction, pasteAction, deselectAction, selectObjectsAction, updateClipboardAction];
			// if clipboard has cut operation data then reset clipboard after paste
			if (clearOnPaste) {
				const resetClipboardAction: IResetClipboardAction = {
					type: ClipboardActionConstants.RESET_CLIPBOARD,
				};
				actions.push(resetClipboardAction);
			}
			return dispatch(batchActions(actions));
		};

/**
 * Set line type action creator.
 * If page object selected then set line type for the object else create action to set shape default properties.
 * @param lineType {CompoundLine} object line type (Solid Thik, Thin etc.)
 * @return {AnyAction}
 */
export const setLineType: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(lineType: CompoundLine) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setLineTypeAction = {
					type: PageActionConstants.SET_LINE_TYPE,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					lineType,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setLineTypeAction]));
			} else {
				const shapeProperties = {
					spPr: {
						ln: {
							cmpd: lineType,
						},
					},
				};
				return dispatch({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
		};

/**
 * Set object stroke width action creator.
 * If page object selected then set stroke width for the object else create action to set shape default properties.
 * @param strokeWidth {number} stroke width
 * @return {AnyAction}
 */
export const setStrokeWidth: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(strokeWidth: number) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setStrokeWidthAction = {
					type: PageActionConstants.SET_STROKE_WIDTH,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					strokeWidth,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setStrokeWidthAction]));
			} else {
				const shapeProperties = {
					spPr: {
						ln: {
							W: strokeWidth,
						},
					},
				};
				return dispatch({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
		};

/**
 * Set line cap action creator.
 * @param headEnd {LineCap}
 * @param tailEnd {LineCap}
 * @return {AnyAction}
 */
export const setLineCap: ActionCreator<ThunkAction<AnyAction, ICanvasEditorState, void, AnyAction>> =
	(headEnd: LineCap, tailEnd: LineCap) =>
		(dispatch: Dispatch<AnyAction>, getState: () => ICanvasEditorState): AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (selectedPageObjects.length && selectedPage) {
				const setLineCapAction = {
					type: PageActionConstants.SET_LINE_CAP,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					headEnd,
					tailEnd,
				};
				// Select object by index action called to keep track of selection in undo/redo stack
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, setLineCapAction]));
			} else {
				const shapeProperties = {
					spPr: {
						ln: {
							headEnd,
							tailEnd,
						},
					},
				};
				return dispatch({
					type: SelectionConstants.SET_SHAPE_DEFAULT_PROPERTIES,
					shapeProperties,
				});
			}
		};

/*
 * Group the currently selected objects that are not locked.
 * If no objects are selected, or if only one object is selected,
 * then this action method will have no effect on state.
 *
 * An ActionCreator function is returned. The ActionCreator function
 * is passed the dispatch method and a method to get the state, and the
 * ActionCreator function returns a BatchAction, allowing multiple actions
 * to be executed sequentially. The end result of these multiple actions
 * is the new group, which is selected after creation.
 */
export const group: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	() => (dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		// selectedPageObjects are indices of objects selected on page,
		// where indices are order in which objects were added to page.
		// Indices are adjusted as objects are added to/removed from page.
		const { selectedPage, selectedPageObjects } = state.selection.present;
		const page = getSelectedPage(state) as IPage;

		if (!page) {
			throw "ERROR: There is no page selected to perform group action.";
		}

		// Filter out locked selected objects, as locked selected objects are not added to the group
		const unlockedSelectedPageObjectIndices: number[] = selectedPageObjects.filter((selectedObject: number) => {
			return !page.pageObjects[selectedObject].spPr.locked;
		});

		if (!(unlockedSelectedPageObjectIndices.length > 1) || !selectedPage) {
			throw "ERROR: Page and at least two object should be selected to perform group action.";
		}

		// Deselect all objects because after group creation, only the new group will be selected
		const deselectAllAction: IDeselectAllObjectAction = {
			type: SelectionConstants.DESELECT_ALL_OBJECTS,
		};

		// Set aspect ratio of group shape, using unlocked page objects to be in group
		const pageObjectsToBeGrouped = _.filter(page.pageObjects, (value, index) =>
			undefined !== _.find(unlockedSelectedPageObjectIndices, (selectionIndex) => selectionIndex === index));

		const result: { off: IOffset; ext: IExtents } =
			manipulateDimensions.forMinOffsetsMaxExtents(pageObjectsToBeGrouped);
		const groupAspectRatio = result.ext.cx / result.ext.cy;

		// Store object selections by index to keep track of selections in undo/redo stack
		const storeObjectSelectionsByIndexAction: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		// Object selection request should not be more than that of available page objects, post group.
		const objectCounts = page.pageObjects.length;
		const maxObjectCount = objectCounts - selectedPageObjects.length;

		const selectNewGroupAction: ISelectObjectAction = {
			isMultiSelect: false,
			selectedObject: Math.min(unlockedSelectedPageObjectIndices[0], maxObjectCount),
			transient: true,
			type: SelectionConstants.SELECT_OBJECT,
		};

		const groupShapeId = generateUUID();
		const createGroupAction: IGroupAction = {
			selectedPage,
			selectedPageObjects: unlockedSelectedPageObjectIndices,
			shapeId: groupShapeId,
			type: PageActionConstants.GROUP,
		};
		const setGroupAspectRatioAction: ISetAspectRatioAction = {
			aspectRatio: groupAspectRatio,
			isMultiSelect: false,
			shapeId: groupShapeId,
			transient: true,
			type: SelectionConstants.SET_ASPECT_RATIO,
		};
		/* Batch actions are executed sequentially:
		 *   - store selected object indices for undo/redo stack
		 *   - deselect all current selections
		 *   - create the group
		 *   - select the new group object
		 *   - set the aspect ratio of the new group object
		*/
		return dispatch(batchActions([
			storeObjectSelectionsByIndexAction, deselectAllAction, createGroupAction,
			selectNewGroupAction, setGroupAspectRatioAction,
		]));
	};

/**
 * Ungroup selection
 */
export const ungroup: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	() => (dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage, selectedPageObjects } = state.selection.present;
		if (!(selectedPageObjects.length) || !selectedPage) {
			throw "ERROR: Page and at least one object should be selected to perform ungroup action.";
		}
		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform ungroup action.";
		}
		const { pageObjects } = page;

		// compute post ungroup selection

		// Group shape selection inventory
		// Use this for maintaining post ungroup selection
		let preUngroupInventory: Array<{
			// Holder for group shape index in pageObjects
			pageObjectIndex: number;
			// Holder for group shape index in selectedPageObjects
			selectionIndex: number | undefined;
			// Number of children
			childLength: number;
		}> = [];
		const groupdShapes: number[] = [];
		let offsetIndex = 0;
		_.forEach(pageObjects, (pageObject, pageObjectIndex) => {
			_.forEach(selectedPageObjects, (spoi, selectionIndex) => {
				const found = (spoi === pageObjectIndex &&
					pageObject.type === IPageObjectTypes.GROUP_SHAPE &&
					!pageObject.spPr.locked && !pageObject.isAttributePlaceholder);
				if (found) {
					groupdShapes.push(pageObjectIndex + offsetIndex);
					const childLength = (pageObject as IGroupShape).childObjects.length;
					offsetIndex += childLength - 1;
					preUngroupInventory.push({
						childLength,
						pageObjectIndex,
						selectionIndex,
					});
				}
			});
		});

		// Sort selection inventory before computing post selection state
		preUngroupInventory = _.reverse(_.sortBy(preUngroupInventory, (entry) => entry.pageObjectIndex));

		// form the new selection, preserving earlier non group page objects
		// and replacing group shapes with corresponding children
		let newSelection = [...selectedPageObjects];

		// Set aspect ratios of all selected page objects after ungroup
		const aspectRatioMap: { [key: string]: number } = {};
		_.each(newSelection, (selectedObject) => {
			const pageObject = pageObjects[selectedObject];
			if (pageObject.type === IPageObjectTypes.GROUP_SHAPE &&
				!pageObject.spPr.locked) {
				const childObjects = (pageObject as IGroupShape).childObjects;

				_.each(childObjects, (childObject) => {
					aspectRatioMap[childObject.shapeId] =
						childObject.spPr.xfrm.ext.cx / childObject.spPr.xfrm.ext.cy;
				});
			} else {
				aspectRatioMap[pageObject.shapeId] =
					pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
			}
		});
		const setAllAspectRatiosAction: ISetAllAspectRatiosAction = {
			aspectRatioMap,
			transient: true,
			type: SelectionConstants.SET_ALL_ASPECT_RATIOS,
		};

		_.forEach(preUngroupInventory, (entry) => {
			// all the entries in newSelection, where  page object index is greater than entry.pageObjectIndex,
			// will get incremented by entry.childLength - 1
			newSelection = newSelection.map((s) => s > entry.pageObjectIndex ? s + entry.childLength - 1 : s);

			// update selection list with ungrouped shapes. Position will start where group shape was present earlier
			for (let count = entry.childLength - 1; count > 0; count--) {
				newSelection.splice(entry.selectionIndex as number + 1, 0, entry.pageObjectIndex + count);
			}
		});

		// Select object by index action called to keep track of selection in undo/redo stack
		const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		const deselectAction: IDeselectAllObjectAction = {
			transient: true,
			type: SelectionConstants.DESELECT_ALL_OBJECTS,
		};
		const ungroupAction: IUnGroupAction = {
			groupdShapes,
			selectedPage,
			type: PageActionConstants.UNGROUP,
		};
		const selectObjectsAction: ISelectObjectsByIndexAction = {
			selectedPageObjects: newSelection,
			transient: true,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};
		return dispatch(batchActions([selectObjectsByIndexAction, deselectAction, ungroupAction,
			selectObjectsAction, setAllAspectRatiosAction]));
	};

/**
 * Rotate object action creator.
 * @param rotateAngle {number} rotate angle
 * @return {IRotateObjectAction}
 */
export const rotateObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(rotateAngle: number, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			let actions: any[] = [];
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			let { groupSelectionIndices } = state.selection.present;
			const page = getSelectedPage(state);
			let groupObject = undefined;

			if (groupSelectionIndices === undefined) {
				groupSelectionIndices = [];
			}

			if (!page) {
				throw "ERROR: No page selected to perform translate object operation";
			}

			const { pageObjects } = page;
			let selectedObject = pageObjects.filter((pageObject, index) => {
				return selectedPageObjects.indexOf(index) !== -1;
			})[0];

			// Unselected page objects
			const unselectedObjects: PageObjectType[] = pageObjects.filter((pageObject, index) => {
				return selectedPageObjects.indexOf(index) === -1;
			});

			if (groupSelectionIndices.length > 0 && selectedPageObjects.length === 1) {
				groupObject = selectedObject as IGroupShape;
				selectedObject = groupObject.childObjects[groupSelectionIndices[0]];
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform rotate action.";
			}

			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				transient,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const rotateObjectAction: IRotateObjectAction = {
				type: PageActionConstants.ROTATE_OBJECT,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				rotateAngle,
				transient,
			};

			actions = actions.concat(selectObjectsByIndexAction, rotateObjectAction);

			if (!transient) {
				actions = actions.concat(createGuidelineActions(transient));
			} else {
				const isSnapToGuide = state.canvasEditor.isSnapToGuide;
				if (isSnapToGuide) {
					const guidesObj = getGuidesPoints(state, groupObject || selectedObject, unselectedObjects, 0, 0);
					actions = actions.concat(createGuidelineActions(transient, false, isSnapToGuide, guidesObj));
				}
			}

			return dispatch(batchActions(actions));
		};

/**
 * Sort pages as per the array of pages id's
 * @param sortList {string[]}
 * @return {BatchAction}
 */
export const sortPages: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(sortList: string[]) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const currentPage = getSelectedPage(state) as IPage;
			const newState = _.cloneDeep(state);
			const order: any = {};

			sortList.forEach((a, i) => { order[a] = i; });

			newState.pages.present.sort((a, b) => {
				return order[a.id] - order[b.id];
			});
			// find new index of selected page
			const newPageIndex = _.findIndex(newState.pages.present, (page) => { return page.id === currentPage.id; });
			const sortPagesAction: ISortPagesAction = {
				sortedPages: newState.pages.present,
				type: PageActionConstants.SORT_PAGES,
			};
			const selectPageAction: ISelectPageAction = {
				selectedPage: newPageIndex + 1,
				type: SelectionConstants.SELECT_PAGE,
			};
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects: [],
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, sortPagesAction, selectPageAction]));
		};
/**
 * Set offsets action creator
 * @param off Offsets to set
 * @return Set offsets action
 */
export const setOffsets: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(off: IOffset) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform set offset action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const setOffSetAction: ISetOffsetsAction = {
				type: PageActionConstants.SET_OFFSET,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				off,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, setOffSetAction]));
		};

/**
 * Flip object action creator.
 * @param flipDetails {IObjectFlip} flipV and flipH
 * @return {IRotateObjectAction}
 */
export const flipObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(flipDetails: IObjectFlip) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform flip action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const flipAction: IFlipObjectAction = {
				type: PageActionConstants.FLIP_OBJECTS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				flipDetails,
			};
			return dispatch(batchActions([selectAction, flipAction]));
		};

/**
 * Set line dash action creator.
 * If page object selected then set line dash for the object else create action to set shape default properties.
 * @param dashType {string} line dash type
 * @return {BatchAction}
 */
export const setLineDash: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(dashType: DashPresets) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			const action: AnyAction[] = [];
			if (selectedPageObjects.length && selectedPage) {
				// Select object by index action called to keep track of selection in undo/redo stack
				action.push({
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				});
				action.push({
					type: PageActionConstants.SET_LINE_DASH,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					dashType,
				});
			} else {
				const shapeProperties = {
					spPr: {
						ln: {
							dash: dashType,
						},
					},
				};
				action.push({
					type: stateConstants().getSetDefaultActionType,
					shapeProperties,
				});
			}
			return dispatch(batchActions(action));
		};

/**
 * Align object action creator.
 * @param alignOptions {string} align option
 * @return {BatchAction}
 */
export const alignObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(alignOptions: string) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			const { editorHeight, editorWidth } = state.canvasEditor;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform align action.";
			}
			const alignObjectAction: IAlignObjectAction = {
				type: PageActionConstants.ALIGN_OBJECTS,
				groupSelectionIndices,
				selectedPageObjects,
				selectedPage,
				alignOptions,
				editorHeight,
				editorWidth,
			};
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			return dispatch(batchActions([selectAction, alignObjectAction]));
		};

/**
 * Scale Objects action creator.
 * @param scalePointType Scale point type (position)
 * @param delta Abstract object holding delta values for scaling purpose
 * @param currentPosition current pointer position
 * @param bCenter Flag to scale with respect to object center
 * @param isMaintainAspectRatio Flag to indicate if Aspect ratio needs to maintain on scale
 * @param refExt maintain Extents WRT source rectangle
 * @param isGhostImageScale Flag to indicate if scaling operation is under picture edit
 * and scaling is being performed for image being cropped
 * @param {boolean} transient
 */
export const scaleObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(
		scalePointType: PageObjectScalePointType, delta: IPosition, currentPosition: IPosition, bCenter: boolean,
		isMaintainAspectRatio: boolean, shapeId: string, refExt: IExtents, transient?: boolean,
		isGhostImageScale?: boolean | undefined, groupId?: string | undefined,
	) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			let actions: any[] = [];
			const state: ICanvasEditorState = getState();
			const currentPage = getSelectedPage(state);
			const pageObjects = getSelectedPageObjects(state) as PageObjectType[];
			let po: PageObjectType;
			if (groupId) {
				const go = _.find(pageObjects, { shapeId: groupId }) as any as IGroupShape;
				po = _.find(go.childObjects, { shapeId }) as PageObjectType;
			} else {
				po = _.find(pageObjects, { shapeId }) as PageObjectType;
			}
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage || !currentPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}

			let type = PageActionConstants.SCALE;
			let isCroppingShapeScale = false;
			// IF canvas is in EDIT_PICTURE then either image or cropping shape scaling will be in action
			if (state.canvasEditor.mode === CanvasMode.EDIT_PICTURE) {
				if (isGhostImageScale) {
					// In this case, cropped image is being scaled and cropping shape object remains as it is,
					// dimension and position wise.
					isCroppingShapeScale = false;
					type = PageActionConstants.SCALE_CROPPED_IMAGE;
				} else {
					// In this case, cropped image remains as it is, and cropping shape is scaled.
					isCroppingShapeScale = true;
				}
			}

			// Remove migrated extends if any with selected objects
			if (pageObjects) {
				const shapeIds = getMigratedShapeIds(pageObjects);
				if (shapeIds.length) {
					actions.push({
						transient: true,
						shapeIds,
						type: PageActionConstants.REMOVE_MIGRATED_EXTENTS,
					} as IRemoveMigratedExtents);
				}
			}

			let aspectRatio = 0;

			// Select object by index action called to keep track of selection in undo/redo stack
			actions.unshift({
				selectedPageObjects,
				transient,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			} as ISelectObjectsByIndexAction);

			let groupObject: IGroupShape | undefined;
			let groupObjectIndex: number | undefined;
			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				groupObject = currentPage.pageObjects[selectedPageObjects[0]] as any as IGroupShape;
				groupObjectIndex = selectedPageObjects[0];
			} else {
				groupObject = undefined;
			}

			// We are handling ghost image scale separately, since in this case we won't have multiple shape selection.
			// Also we need to find aspect ratio for the image and not cropped shape.
			if (isGhostImageScale) {
				const imageExtents = getImageExtents(po as IShape);
				aspectRatio = imageExtents.cx / imageExtents.cy;
				const ghostImageScale =
					manipulateDimensions.resizeObject(
po as PageObjectType, scalePointType, true, bCenter,
delta, aspectRatio, true, null, groupObject,
					);
				const scaleObjectAction: IScaleObjectAction = {
					cropDHeight: ghostImageScale.cropDHeight,
					cropDWidth: ghostImageScale.cropDWidth,
					cropDX: ghostImageScale.cropDX,
					cropDY: ghostImageScale.cropDY,
					dHeight: ghostImageScale.dHeight,
					dWidth: ghostImageScale.dWidth,
					dX: ghostImageScale.dX,
					dY: ghostImageScale.dY,
					isCroppingShapeScale,
					mode: state.canvasEditor.mode,
					refExt,
					groupSelectionIndices,
					selectedPage,
					selectedPageObjects,
					transient,
					type,
				};
				actions.push(scaleObjectAction);
			} else {
				if (isMaintainAspectRatio && state.selection.present.aspectRatioMap) {
					aspectRatio = state.selection.present.aspectRatioMap[po.shapeId];
				}

				if (groupObject) {
					// Subgroup element aspect ratio will not be in the map
					aspectRatio = po.spPr.xfrm.ext.cx / po.spPr.xfrm.ext.cy;
				}

				// we will pass this reference scale to other objects to be resized.
				const refScale = manipulateDimensions.resizeObject(
po as PageObjectType, scalePointType,
isMaintainAspectRatio, bCenter, delta, aspectRatio, false, null, groupObject,
				);

				let pageObjectsToScale = selectedPageObjects;
				if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0 && groupObject) {
					pageObjectsToScale = groupSelectionIndices;
				}

				// multi select and image scale are mutually exclusive.
				_.each(pageObjectsToScale, (selectedPageObjectIndex) => {
					// This condition is just added for lint error.
					if (!currentPage) {
						return;
					}

					let pageObjectToScale;
					if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0 && groupObject) {
						pageObjectToScale = groupObject.childObjects[selectedPageObjectIndex];
					} else {
						pageObjectToScale = currentPage.pageObjects[selectedPageObjectIndex];
					}

					// If we are resizing without maintaining aspect ratio (without shift key)
					// and selected object forces to maintain aspect ration, then ignore the resize for the shape.
					if (!isMaintainAspectRatio && pageObjectToScale.noChangeAspect
						&& (state.canvasEditor.mode !== CanvasMode.EDIT_PICTURE)) {
						return;
					}

					// Currently we don't support resize a shape using non-corner if it enforces noChangeAspect ratio.
					const isCorner = isCornerScalePoint(scalePointType);
					if (isMaintainAspectRatio && !isCorner) {
						return;
					}

					// if we are not maintaining aspect ratio then we will use same reference scale for all objects.
					let resizeMeta = _.clone(refScale);

					// Calculate shape resize meta specific to shape, if we are maintaining aspect ratio.
					if (isMaintainAspectRatio
						&& state.selection.present.aspectRatioMap
						&& pageObjectToScale.shapeId !== shapeId) {
						aspectRatio = state.selection.present.aspectRatioMap[pageObjectToScale.shapeId];
						resizeMeta = manipulateDimensions.resizeObject(
pageObjectToScale as PageObjectType,
scalePointType, isMaintainAspectRatio, bCenter,
delta, aspectRatio, false, refScale, groupObject,
						);
					}

					let selectedObject = selectedPageObjectIndex;
					let groupIndices: number[] = [];
					if (groupObjectIndex !== undefined) {
						selectedObject = groupObjectIndex;
						groupIndices = [selectedPageObjectIndex];
					}

					const scaleObjectAction: IScaleObjectAction = {
						cropDHeight: resizeMeta.cropDHeight,
						cropDWidth: resizeMeta.cropDWidth,
						cropDX: resizeMeta.cropDX,
						cropDY: resizeMeta.cropDY,
						dHeight: resizeMeta.dHeight,
						dWidth: resizeMeta.dWidth,
						dX: resizeMeta.dX,
						dY: resizeMeta.dY,
						groupSelectionIndices: groupIndices,
						mode: state.canvasEditor.mode,
						refExt,
						selectedPageObjects: [selectedObject],
						selectedPage,
						isCroppingShapeScale,
						transient,
						type,
					};
					actions.push(scaleObjectAction);
				});
			}

			if (!transient) {
				actions = actions.concat(createGuidelineActions(transient));
			} else {
				const currentPageObjects = currentPage.pageObjects;

				const selectedObject = currentPageObjects.filter((pageObject, index) => {
					return selectedPageObjects.indexOf(index) !== -1;
				})[0];

				// Unselected page objects
				const unselectedObjects: PageObjectType[] = currentPageObjects.filter((pageObject, index) => {
					return selectedPageObjects.indexOf(index) === -1;
				});

				const isSnapToGuide = state.canvasEditor.isSnapToGuide;
				if (isSnapToGuide) {
					const guidesObj = getGuidesPoints(state, selectedObject, unselectedObjects, 0, 0);
					actions = actions.concat(createGuidelineActions(transient, false, isSnapToGuide, guidesObj));
				}
			}

			return dispatch(batchActions(actions));
		};

/**
 * Apply format painter action creator.
 * @param targetObjectIndex {string} target object shapeId
 * @return {BatchAction}
 */
export const applyFormatPainter: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(targetShapeId: string) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage,
				formatPainterTextStyles } = state.selection.present;
			const { isFormatPainterActive, placeholderTextFormat } = state.canvasEditor;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage || !isFormatPainterActive) {
				throw ("ERROR: Page, format painter option and at least one object should be selected to perform this action.");
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			// apply format painter action
			const formatPainterAction: IFormatPainterAction = {
				placeholderTextFormat,
				sourceShapeTextStyle: formatPainterTextStyles,
				groupSelectionIndices,
				selectedPageObjects,
				selectedPage,
				type: PageActionConstants.APPLY_FORMAT_PAINTER,
				targetShapeId,
			};

			// end format painter by setting format painter flag to false
			const endFormatPainter: ISetFormatPainterAction = {
				transient: true,
				isFormatPainter: false,
				type: CanvasEditorConstants.SET_FORMAT_PAINTER,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, formatPainterAction, endFormatPainter]));
		};

/**
 * Lock objects action creator.
 * @param lock {boolean} true : Lock object, false: unlock objects
 * @return {BatchAction}
 */
export const lockObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(lock: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform lock object action.";
			}

			// Set canvas mode to select before locking objects
			const setCanvasModeAction = {
				mode: CanvasMode.SELECT,
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
			} as ISetCanvasModeAction;

			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const lockObjectAction: ILockObjectAction = {
				type: PageActionConstants.LOCK_OBJECTS,
				selectedPageObjects,
				selectedPage,
				lock,
			};
			return dispatch(batchActions([setCanvasModeAction, selectObjectsByIndexAction, lockObjectAction]));
		};

/**
 * Distribute Object action creator.
 * @param dHorizontal {boolean} distribute option, true- horizontal distribution, false- vertical distribution
 * @return {BatchAction}
 */
export const distributeObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(dHorizontal: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (selectedPageObjects.length < 3 || !selectedPage) {
				throw "ERROR: Page and at least 3 objects should be selected to perform distribute action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const distributeObjectAction: IDistributeObjectAction = {
				type: PageActionConstants.DISTRIBUTE_OBJECTS,
				selectedPageObjects,
				selectedPage,
				dHorizontal,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, distributeObjectAction]));
		};

/**
 * Add page object action creator. This is batch action, it will create add object and select object action.
 * We want to selected added page object.
 * @param pageObject {PageObjectType} Page object to be added.
 * @param setAspectRatio {boolean} Flag determining if aspect ratio should be set.
 * @param forceUID {boolean} Flag indicating if page object and its children should get unique shapeIds.
 * @returns {BatchAction}
 */
export const addPageObject: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(
		pageObject: PageObjectType,
		setAspectRatio: boolean = true,
		forceUID: boolean = false,
	) => (
		dispatch: Dispatch<BatchAction>,
		getState: () => ICanvasEditorState,
	): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage } = state.selection.present;

		if (!selectedPage) {
			throw "ERROR: Page must be selected to perform add object action.";
		}

		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform add object action.";
		}

		const objectCounts = page.pageObjects.length;
		const aspectRatio = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;

		// Set UIDs if necessary.
		if (forceUID) {
			deepGenerateNewUUIDs([pageObject]);
		}

		// We want to select currently added object.
		const selectAction: ISelectObjectAction = {
			isMultiSelect: false,
			selectedObject: objectCounts,
			transient: true,
			type: SelectionConstants.SELECT_OBJECT,
		};
		const setAspectRatioAction: ISetAspectRatioAction = {
			isMultiSelect: false,
			shapeId: pageObject.shapeId,
			aspectRatio,
			transient: true,
			type: SelectionConstants.SET_ASPECT_RATIO,
		};
		const addPageObjectAction: IAddPageObjectAction = {
			type: PageActionConstants.ADD_PAGE_OBJECT,
			pageObject,
			selectedPage,
		};
			// Select object by index action called to keep track of selection in undo/redo stack
		const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
			selectedPageObjects: [objectCounts],
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		const actions: Action[] = [selectObjectsByIndexAction, addPageObjectAction, selectAction];

		if (setAspectRatio) {
			actions.push(setAspectRatioAction);
		}

		return dispatch(batchActions(actions));
	};

/**
 * Add image action creator.
 * This action creator wraps (abstracts out) addPageObject action creator with specifics to image.
 * @param x Image x offset position
 * @param y Image y offset position
 * @param width Image width
 * @param height Image height
 * @param imageLink Image url
 * @returns addPageObject action creator with pre-decided pageObject structure suitable for image as page object
 */
export const addImage: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(x: number, y: number, width: number, height: number, imageLink: string) => {
		const pageObject: PageObjectType = {
			isPicture: true,
			noChangeAspect: false,
			shapeId: generateUUID(),
			spPr: {
				fill: {
					blip: {
						link: imageLink,
					},
					srcRect: {
						b: 0,
						flipH: false,
						l: 0,
						r: 0,
						refExt: { cx: width, cy: height },
						rot: 0,
						t: 0,
					},
					type: FillTypes.PICTURE_FILL,
				},
				geometry: {
					presetShape: ShapeType.rect,
					type: GeometryTypes.PRESET,
				},
				ln: {
					fill: {
						type: FillTypes.NO_FILL,
					},
				},
				xfrm: {
					ext: { cx: width, cy: height },
					flipH: false,
					flipV: false,
					off: {
						x,
						y,
					},
					rot: 0,
				},
			},
			type: IPageObjectTypes.SHAPE,
		};
		return addPageObject(pageObject);
	};

/**
 * Add image action creator.
 * This action creator wraps (abstracts out) addPageObject action creator with specifics to image.
 * @param x Image x offset position
 * @param y Image y offset position
 * @param width Image width
 * @param height Image height
 * @param imageLink Image url
 * @returns addPageObject action creator with pre-decided pageObject structure suitable for image as page object
 */
export const addImages: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(images: IAddImageRequest[]) => (
		dispatch: Dispatch<BatchAction>,
		getState: () => ICanvasEditorState,
	): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage, selectedPageObjects } = state.selection.present;

		if (!selectedPage) {
			throw "ERROR: Page must be selected to perform add object action.";
		}

		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform add object action.";
		}

		let objectCounts = page.pageObjects.length;

		const pageObjects: IShape[] = _.map(images, (image) => {
			return getImageObject(image);
		});

		const newSelection = _.map(images, () => objectCounts++);

		// Set aspect ratios of all added images
		const aspectRatioMap: { [key: string]: number } = {};
		_.each(pageObjects, (pageObject) => {
			aspectRatioMap[pageObject.shapeId] = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
		});

		const setAllAspectRatiosAction: ISetAllAspectRatiosAction = {
			aspectRatioMap,
			transient: true,
			type: SelectionConstants.SET_ALL_ASPECT_RATIOS,
		};

		// We want to select currently added objects.
		const selectAction: ISelectObjectsByIndexAction = {
			selectedPageObjects: newSelection,
			transient: true,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};
		const addPageObjectAction: IAddPageObjectsAction = {
			type: PageActionConstants.ADD_PAGE_OBJECTS,
			pageObjects,
			selectedPage,
		};
		// Select object by index action called to keep track of selection in undo/redo stack
		const storeObjectSelection: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		return dispatch(batchActions([storeObjectSelection, addPageObjectAction, selectAction,
			setAllAspectRatiosAction]));
	};

/**
 * Add image to placeholder action creator.
 * @param image image to be added to placeholder
 * @param id id of placeholder to add image to
 * @returns addPageObject action creator with pre-decided pageObject structure suitable for image as page object
 */
export const addImageToPlaceholder: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(image: IAddImageRequest, id: String, noSelect?: boolean) => (
		dispatch: Dispatch<BatchAction>,
		getState: () => ICanvasEditorState,
	): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage, selectedPageObjects } = state.selection.present;

		if (!selectedPage) {
			throw "ERROR: Page must be selected to perform add object action.";
		}

		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform add object action.";
		}

		const objectsToSelect = deepFindOutermostGroup(page.pageObjects, id);

		const addImageToPlaceholderAction: IAddImageToPlaceholderAction = {
			objectId: id,
			type: PageActionConstants.ADD_IMAGE_TO_PLACEHOLDER,
			image,
			selectedPage,
		};

		if (noSelect) {
			return dispatch(batchActions([addImageToPlaceholderAction]));
		}
		// Select object by index action called to keep track of selection in undo/redo stack
		const storeObjectSelection: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		const selectAction: ISelectObjectsByIndexAction = {
			groupSelectionIndices: objectsToSelect.checkingSelectionIndices,
			selectedPageObjects: [objectsToSelect.checkingObject],
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		return dispatch(batchActions([storeObjectSelection, addImageToPlaceholderAction, selectAction]));
	};

/**
 * Add attributes to placeholder action creator.
 * @param attributes objects containing attributes to be added to placeholder and permission variable
 * @param id id of placeholder to add placeholder to
 * @param node url of the node which the filling attributes come from
 * @returns addPageObject action creator
 */
export const fillPlaceholderWithAttributes: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(attributes: IPlaceholderFillData[], id: String, node: string) => (
		dispatch: Dispatch<BatchAction>,
		getState: () => ICanvasEditorState,
	): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { selectedPage, selectedPageObjects } = state.selection.present;

		if (!selectedPage) {
			throw "ERROR: Page must be selected to perform add object action.";
		}

		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform add object action.";
		}

		const addAttributeToPlaceholderAction: IAddAttributeToPlaceholderAction = {
			objectId: id,
			type: PageActionConstants.ADD_ATTRIBUTE_TO_PLACEHOLDER,
			attributes,
			node,
			selectedPage,
		};

		const resetPlaceholderExtendsAction: IResetPlaceholderExtendsAction = {
			objectId: id,
			type: PageActionConstants.RESET_PLACEHOLDER_EXTENDS,
			selectedPage,
			selectedPageObjects,
		};

		// Select object by index action called to keep track of selection in undo/redo stack
		const storeObjectSelection: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		const objectsToSelect = deepFindOutermostGroup(page.pageObjects, id);

		const selectAction: ISelectObjectsByIndexAction = {
			groupSelectionIndices: objectsToSelect.checkingSelectionIndices,
			selectedPageObjects: [objectsToSelect.checkingObject],
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		return dispatch(batchActions([
			storeObjectSelection,
			addAttributeToPlaceholderAction,
			resetPlaceholderExtendsAction,
			selectAction,
		]));
	};

/**
 * Add placeholders action creator.
 * This action creator returns a batch of actions to add placeholder shapes and
 * to put the shapes in a group.
 * @param placeholderData Object data used to create text and/or image placeholders
 * @returns batched actions to create placeholders and put them in a group
 *
 * This method batch dispatches these actions, including some modeled
 * from addImages() and others from group().
 * - storeObjectSelectionsByIndexAction (store object selections for undo/redo)
 * - deselectAllAction (deselect all objects as finally group will be selected)
 * - addPageObjectAction (add page objects for the placeholder text/image objects)
 * - setAllAspectRatiosAction (set aspect ratios for the new objects)
 * - createGroupAction (create group from added placeholder text and image shapes)
 * - selectNewGroupAction (select the new group containing placeholder objects)
 * - setGroupAspectRatioAction (set aspect ratio for the group object)
 */
export const addPlaceholders: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(placeholderData: {
		attributeKeys: string[];
		imageFlags: boolean[];
		type: string;
		groupObject?: IGroupShape;
		previousAttributeKeys?: string[];
		position?: IPosition;
	}) => (
		dispatch: Dispatch<BatchAction>,
		getState: () => ICanvasEditorState,
	): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;

		if (!selectedPage) {
			throw "ERROR: Page must be selected to perform add object action.";
		}

		const page = getSelectedPage(state);
		if (!page) {
			throw "ERROR: There is no page selected to perform add object action.";
		}

		// New group will be added at index after existing objects.
		const objectCounts = page.pageObjects.length;

		// Action to store object selections by index to keep track of selections in undo/redo stack.
		// These are shape selections before user chose to create placeholder group.
		const storeObjectSelectionsByIndexAction: ISelectObjectsByIndexAction = {
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		};

		// Action to deselect all objects, in case objects were selected before creating placeholder group.
		// After creating placeholder objects and their group, only the group will be selected.
		const deselectAllAction: IDeselectAllObjectAction = {
			type: SelectionConstants.DESELECT_ALL_OBJECTS,
		};

		// Stores text and image shapes to be added for placeholders.
		let pageObjects: IShape[] = [];

		// Keep the width the same as the group for new objects
		const defaultWidth = placeholderData.groupObject ?
			placeholderData.groupObject.spPr.xfrm.ext.cx : DEFAULT_IMAGE_PLACEHOLDER_WIDTH;

		// Add text shapes for attribute placeholders
		const attributeKeys = placeholderData.attributeKeys;

		let [xPos, yPos] = [0, 0];
		if (placeholderData.position) {
			xPos = placeholderData.position.x;
			yPos = placeholderData.position.y;
		}
		const groupObject = placeholderData.groupObject as IGroupShape;
		let yPosGrp = groupObject?.spPr.xfrm.ext.cy ?? 0;
		// loop through all new attribute keys to create or recreate the attribute placeholder
		_.each(attributeKeys, (attributeKey, i) => {
			// get the index of the previous attribute key based on the current one
			const index = _.indexOf(placeholderData.previousAttributeKeys, attributeKey);
			// consider y offset for autospace
			const yPosAutospace = groupObject?.autospace ? yPos : yPosGrp;
			if (index > -1) {
				// previous subgroup element matching previous attribute key was found, so use it
				const childObject = groupObject.childObjects[index] as IShape;
				pageObjects = pageObjects.concat(childObject);
				yPos += childObject.spPr.xfrm.ext.cy + SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES;
			} else if (placeholderData.imageFlags[i]) {
				// adding an image
				pageObjects = pageObjects.concat(getImagePlaceholderObject({
					attributeKey,
					height: DEFAULT_IMAGE_PLACEHOLDER_HEIGHT,
					type: placeholderData.type,
					width: defaultWidth,
					x: xPos,
					y: yPosAutospace,
				}));
				const offset = DEFAULT_IMAGE_PLACEHOLDER_HEIGHT + SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES;
				yPos += offset;
				yPosGrp += offset;
			} else {
				// adding a text shape
				const attributePlaceholderData = {
					attributeKey,
					height: ATTRIBUTE_PLACEHOLDER_DEFAULT_HEIGHT,
					width: defaultWidth,
					x: xPos,
					y: yPosAutospace,
				};
				pageObjects = pageObjects.concat(getAttributePlaceholderObject(attributePlaceholderData));
				const offset = ATTRIBUTE_PLACEHOLDER_DEFAULT_HEIGHT + SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES;
				yPos += offset;
				yPosGrp += offset;
			}
		});

		if (groupObject) {
			const editPlaceholderAction: IEditPlaceholderAction = {
				type: PageActionConstants.EDIT_PLACEHOLDER,
				groupSelectionIndices,
				selectedPage,
				selectedPageObjects,
				pageObjects,
			};
			return dispatch(batchActions([
				storeObjectSelectionsByIndexAction,
				deselectAllAction,
				editPlaceholderAction,
			]));
		}

		// Action to add the page objects for the placeholder text shapes and image placeholders.
		const addPageObjectAction: IAddPageObjectsAction = {
			type: PageActionConstants.ADD_PAGE_OBJECTS,
			pageObjects,
			selectedPage,
		};

		// Set aspect ratios of all added text shapes and image placeholders
		const aspectRatioMap: { [key: string]: number } = {};

		_.each(pageObjects, (pageObject) => {
			aspectRatioMap[pageObject.shapeId] = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
		});

		// Action to set aspect ratios for placeholder shapes to be added.
		const setAllAspectRatiosAction: ISetAllAspectRatiosAction = {
			aspectRatioMap,
			transient: true,
			type: SelectionConstants.SET_ALL_ASPECT_RATIOS,
		};

		// Assign unique group shape ID to new group
		const groupShapeId = generateUUID();

		// Indexes of objects added are after last current object.
		const newObjectIndexes: number[] = [];

		pageObjects.map(() => {
			newObjectIndexes.push(objectCounts + newObjectIndexes.length);
		});

		const createGroupAction: IGroupAction = {
			autospace: true,
			isAttributePlaceholder: true,
			selectedPage,
			selectedPageObjects: newObjectIndexes,
			shapeId: groupShapeId,
			type: PageActionConstants.GROUP,
		};

		const result: { off: IOffset; ext: IExtents } =
				manipulateDimensions.forMinOffsetsMaxExtents(pageObjects);
		const groupAspectRatio = result.ext.cx / result.ext.cy;

		const setGroupAspectRatioAction: ISetAspectRatioAction = {
			aspectRatio: groupAspectRatio,
			isMultiSelect: false,
			shapeId: groupShapeId,
			transient: true,
			type: SelectionConstants.SET_ASPECT_RATIO,
		};

		// Select the group in which the placeholder objects were created.
		// Group shape index is right after objects that existed before
		// the group was created.
		const selectNewGroupAction: ISelectObjectAction = {
			isMultiSelect: false,
			selectedObject: objectCounts,
			transient: true,
			type: SelectionConstants.SELECT_OBJECT,
		};

		const resetPlaceholderExtendsAction: IResetPlaceholderExtendsAction = {
			objectId: groupShapeId,
			type: PageActionConstants.RESET_PLACEHOLDER_EXTENDS,
			selectedPage,
			selectedPageObjects,
		};

		// C8-18849
		// reset the canvas mode so another shape can be selected immediately
		const resetCanvasMode: ISetCanvasModeAction = {
			mode: CanvasMode.SELECT,
			transient: true,
			type: CanvasEditorConstants.SET_CANVAS_MODE,
		};

		// These batched actions will execute sequentially, as they may depend on
		// previous actions completing.
		return dispatch(batchActions([
			storeObjectSelectionsByIndexAction,
			deselectAllAction,
			addPageObjectAction,
			setAllAspectRatiosAction,
			createGroupAction,
			selectNewGroupAction,
			setGroupAspectRatioAction,
			resetPlaceholderExtendsAction,
			resetCanvasMode,
		]));
	};

/**
 * Set shape adjust values
 * @param dx Delta movement along x axis
 * @param dy Delta movement along y axis
 * @param adjustHandler Target shape adjust handler
 * @param avList Adjust value list
 */
export const setShapeAdjustValues: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(
		dx: number, dy: number, adjustHandler: IXYAdjustHandle, avList: IShapeGuide[], refExt: IExtents,
		transient: boolean,
	) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState):
			BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			let selectedObjects: PageObjectType[] = getSelectedPageObjects(state) as PageObjectType[];
			let groupObject = undefined;
			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				groupObject = (selectedObjects[0] as IGroupShape);
				selectedObjects = getIndexedChildren(groupObject.childObjects, groupSelectionIndices);
			}
			selectedObjects = _.filter(selectedObjects, (pageObject) => pageObject.type === IPageObjectTypes.SHAPE &&
				pageObject.spPr.geometry.type === GeometryTypes.PRESET && !pageObject.spPr.locked);

			if (!(selectedObjects.length === 1)) {
				throw "ERROR: Page and one object should be selected to perform adjust shape action.";
			}
			const pageObject = _.first(selectedObjects) as IShape;
			const { x, y } = mapGroupedPointToShapeMatrix(groupObject, pageObject, { x: dx, y: dy });
			dx = x;
			dy = y;
			const finalAvList = _.cloneDeep(pageObject.spPr.geometry.avList ? pageObject.spPr.geometry.avList : avList);

			const { maxX, minX, maxY, minY } = adjustHandler;
			// handle x axix
			if (adjustHandler.gdRefX && maxX !== undefined && minX !== undefined) {
				const adjustValue = _.find(finalAvList, (av) => av.name === adjustHandler.gdRefX) as IShapeGuide;
				const newPosX = (adjustHandler.pos.x as number) + dx;
				let minDimension = pageObject.spPr.xfrm.ext.cx;

				/** Reference to shape dimension for adjust handler value manipulation */
				if (adjustHandler.referenceDimension === IReferenceDimension.SS) {
					minDimension = Math.min(pageObject.spPr.xfrm.ext.cx, pageObject.spPr.xfrm.ext.cy);
				}
				let newPosXPercentage = PERCENTAGE_HUNDRED * newPosX / minDimension;
				if (adjustHandler.referenceDimension === IReferenceDimension.CUSTOM) {
					// custom implementation
					newPosXPercentage = getAdjustHandlerPercentage(newPosX, pageObject);
				} else {
					if (adjustHandler.scaleX) {
						newPosXPercentage = newPosXPercentage * adjustHandler.scaleX;
					}
					if (adjustHandler.directionX === IAdjustHandlerDirection.REVERSE) {
						newPosXPercentage = 100 - newPosXPercentage;
					} else if (adjustHandler.directionX === IAdjustHandlerDirection.CENTER) {
						newPosXPercentage = newPosXPercentage - 50;
					}

					// ms convention to represent percentage in terms of 1000
					newPosXPercentage = Math.max(minX as number, Math.min(newPosXPercentage * 1000, (maxX as number)));
				}
				// The avList whould be always for operation literal value
				adjustValue.fmla.operands[0] = newPosXPercentage;
			}
			if (adjustHandler.gdRefY && maxY !== undefined && minY !== undefined) {
				const adjustValue = _.find(finalAvList, (av) => av.name === adjustHandler.gdRefY) as IShapeGuide;
				const newPosY = (adjustHandler.pos.y as number) + dy;
				let minDimension = pageObject.spPr.xfrm.ext.cy;

				/** Reference to shape dimension for adjust handler value manipulation */
				if (adjustHandler.referenceDimension === IReferenceDimension.SS) {
					minDimension = Math.min(pageObject.spPr.xfrm.ext.cx, pageObject.spPr.xfrm.ext.cy);
				}
				let newPosYPercentage = PERCENTAGE_HUNDRED * newPosY / minDimension;
				if (adjustHandler.referenceDimension === IReferenceDimension.CUSTOM) {
					// custom implementation
					newPosYPercentage = getAdjustHandlerPercentage(newPosY, pageObject);
				} else {
					if (adjustHandler.scaleY) {
						newPosYPercentage = newPosYPercentage * adjustHandler.scaleY;
					}
					if (adjustHandler.directionY === IAdjustHandlerDirection.REVERSE) {
						newPosYPercentage = 100 - newPosYPercentage;
					} else if (adjustHandler.directionY === IAdjustHandlerDirection.CENTER) {
						newPosYPercentage = newPosYPercentage - 50;
					}
					// ms convention to represent percentage in terms of 1000
					newPosYPercentage = Math.max(minY as number, Math.min(newPosYPercentage * 1000, (maxY as number)));
				}
				// The avList whould be always for operation literal value
				adjustValue.fmla.operands[0] = newPosYPercentage;
			}

			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				transient,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			return dispatch(batchActions([selectObjectsByIndexAction, {
				avList: finalAvList,
				type: PageActionConstants.Set_SHAPE_ADJUST_VALUES,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				transient,
			}]));
		};

/**
 * Set Autosize mode on shape(s)
 * If page object selected then set autosize for the object and / or subobjects
 * @param autosize {AutosizeMode} Autosize mode
 * @return {BatchAction}
 */
export const setShapeAutosizeValues: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(autosize: AutosizeMode, resizeForContent: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			const action: AnyAction[] = [];
			if (selectedPageObjects.length && selectedPage) {
				// Select object by index action called to keep track of selection in undo/redo stack
				action.push({
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				});
				action.push({
					type: PageActionConstants.SET_SHAPE_AUTOSIZE_VALUES,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					autosize,
					resizeForContent,
				});
			} else {
				action.push({
					transient: true,
					type: SelectionConstants.SET_TEXT_DEFAULT_STYLES,
					autosize,
				});
			}
			return dispatch(batchActions(action));
		};

/**
 * Push/unshift selected shapes to front or back
 * @param bFront { boolean } true- bring to front, false- send back
 * @return { BatchAction | undefined}
 */
export const changePageObjectOrder: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction>> =
	(bFront: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			let selectObjectsAction: ISelectObjectsByIndexAction | ISelectObjectAction;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform change page object action.";
			}
			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform change page object order action.";
			}
			// return if selection has locked shape
			const selectedObjects = page.pageObjects.filter(filterSelectedObjects(selectedPageObjects));
			const unlockedSelectedObjects = selectedObjects.filter(filterUnLockedObjects);
			if (selectedObjects.length !== unlockedSelectedObjects.length) {
				return;
			}
			let pageObjects: PageObjectType[] = page.pageObjects;
			if (groupSelectionIndices === undefined || groupSelectionIndices.length === 0) {
				pageObjects = moveObjectsToFront(pageObjects, selectedPageObjects, bFront);
				const start = !bFront ? 0 : pageObjects.length - selectedPageObjects.length;
				const toBeSelectedObjects = _.range(start, start + selectedPageObjects.length);

				selectObjectsAction = {
					selectedPageObjects: toBeSelectedObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
			} else {
				const groupObject = pageObjects[selectedPageObjects[0]] as IGroupShape;
				const start = !bFront ? 0 : groupObject.childObjects.length - 1;
				selectObjectsAction = {
					childKey: start,
					selectedObject: selectedPageObjects[0],
					type: SelectionConstants.SELECT_OBJECT,
				};
			}

			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			// Deselect all objects action
			const deselectAction: IDeselectAllObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};

			// Change sort order action
			const changeOrderAction: IChangeOrderAction = {
				transient: false,
				type: PageActionConstants.CHANGE_ORDER,
				bFront,
				groupSelectionIndices,
				selectedPageObjects,
				selectedPage,
				pageObjects,
			};

			return dispatch(batchActions([selectObjectsByIndexAction, changeOrderAction,
				deselectAction, selectObjectsAction]));
		};

/**
 * Set new shape path list to the selected shape. This operation can be performed only for single unlocked curve shape.
 * @param pathLst {IShapePath[]} New shape path list.
 * @param dX {number} delta for x as ratio
 * @param dY {number} delta for y as ratio
 * @param dWidth {number} delta for width
 * @param dHeight {number} delta for height
 * @param transient {boolean} transient flag
 * @param isCropCurve {boolean} true if we are adjusting a custom shape
 * @param shapePathOffsetX {number} delta for x as absolute, used only for cropping
 * @param shapePathOffsetY {number} delta for y as absolute, used only for cropping
 * @return { BatchAction }
 */
export const setCurvePoints: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(
		pathLst: IShapePath[], dX: number, dY: number, dWidth: number, dHeight: number, transient?: boolean,
		shapePathOffsetX?: number, shapePathOffsetY?: number,
	) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			const canvasMode = getCanvasMode(state);

			if (selectedPageObjects.length > 1) {
				throw "ERROR: Only one object must be selected to perform this action.";
			}

			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform set curve points action.";
			}
			const pageObjects: PageObjectType[] = page.pageObjects;
			let selectedPageObjectReference = pageObjects[selectedPageObjects[0]];

			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				const groupReference = selectedPageObjectReference as IGroupShape;
				selectedPageObjectReference = groupReference.childObjects[groupSelectionIndices[0]];
			}

			// selected page object must be of type shape or connection shape
			if (selectedPageObjectReference.type !== IPageObjectTypes.SHAPE
				&& selectedPageObjectReference.type !== IPageObjectTypes.CONNECTION_SHAPE) {
				throw "ERROR: This action can be performed only for Shape object.";
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and one object should be selected to perform this action.";
			}

			const selectedShape = selectedPageObjectReference as IShape | IConnectionShape;

			// Shape geometry has to be custom geometry.
			if (selectedShape.spPr.geometry.type !== GeometryTypes.CUSTOM &&
				selectedShape.spPr.geometry.type !== GeometryTypes.PEN) {
				throw "ERROR: This action can be performed only for Custom Shape object.";
			}

			// Shape should not be locked
			if (selectedShape.spPr.locked) {
				throw "ERROR: This action can not be performed for locked shape.";
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			let isPicture = false;
			if (selectedPageObjectReference.type === IPageObjectTypes.SHAPE) {
				isPicture = selectedPageObjectReference.isPicture || false;
			}

			let cropX = 0;
			let cropY = 0;

			if (isPicture) {
				cropX = shapePathOffsetX || 0;
				cropY = shapePathOffsetY || 0;
			}

			const scaleObject: IScaleObjectAction = {
				cropDHeight: dHeight,
				cropDWidth: dWidth,
				cropDX: cropX,
				cropDY: cropY,
				dHeight,
				dWidth,
				dX,
				dY,
				isCroppingShapeScale: isPicture,
				mode: canvasMode,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				transient,
				type: PageActionConstants.SCALE,
			};
			const setCurvePoint: ISetCurvePointsAction = {
				transient: true,
				type: PageActionConstants.SET_CURVE_POINTS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				pathLst,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, scaleObject, setCurvePoint]));
		};

/**
 * Set the provided list of handle points to the current shape.
 * @param handlePoints: {IPosition[]}
 * @return { BatchAction }
 */
export const setHandlePoints: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(pointLst: IHandlePoint[] | undefined) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;

			if (selectedPageObjects.length > 1) {
				throw "ERROR: Only one object must be selected to perform this action.";
			}

			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform set curve points action.";
			}
			const pageObjects = page.pageObjects;
			let selectedPageObjectReference = pageObjects[selectedPageObjects[0]];

			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				selectedPageObjectReference = (selectedPageObjectReference as IGroupShape).childObjects[groupSelectionIndices[0]];
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and one object should be selected to perform this action.";
			}

			const selectedShape = selectedPageObjectReference as IShape | IConnectionShape;

			// Shape geometry has to be pen or custom geometry.
			if (selectedShape.spPr.geometry.type !== GeometryTypes.CUSTOM &&
				selectedShape.spPr.geometry.type !== GeometryTypes.PEN) {
				throw "ERROR: This action can be performed only for Custom Shape object.";
			}

			// Shape should not be locked
			if (selectedShape.spPr.locked) {
				throw "ERROR: This action can not be performed for locked shape.";
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			const setCurvePoint: ISetHandlePointsAction = {
				transient: true,
				type: PageActionConstants.SET_HANDLE_POINTS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				pointLst,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, setCurvePoint]));
		};

/**
* Set new shape path list to the selected shape. 
* @param pathLst {IShapePath[]} New shape path list.
* @return { BatchAction }
*/
export const setShapeGeometry: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(pathLst: IShapePath[]) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;

			if (selectedPageObjects.length > 1) {
				throw "ERROR: Only one object must be selected to perform this action.";
			}

			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform set curve points action.";
			}
			const pageObjects: PageObjectType[] = page.pageObjects;
			let selectedPageObjectReference = pageObjects[selectedPageObjects[0]];

			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				selectedPageObjectReference = (selectedPageObjectReference as IGroupShape).childObjects[groupSelectionIndices[0]];
			}

			// selected page object must be of type shape or connection shape
			if (selectedPageObjectReference.type !== IPageObjectTypes.SHAPE
				&& selectedPageObjectReference.type !== IPageObjectTypes.CONNECTION_SHAPE) {
				throw "ERROR: This action can be performed only for Shape object.";
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and one object should be selected to perform this action.";
			}

			const selectedShape = selectedPageObjectReference as IShape | IConnectionShape;

			// Shape should not be locked
			if (selectedShape.spPr.locked) {
				throw "ERROR: This action can not be performed for locked shape.";
			}

			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}
			// Select object by index action called to keep track of selection in undo/redo stack
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			const setGeometry: ISetGeometryAction = {
				transient: true,
				type: PageActionConstants.SET_GEOMETRY,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				pathLst,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, setGeometry]));
		};

/**
 * Set shape text content for the shape at specified index. This action can only be performed if shape is not locked,
 * shape geometry is PRESET.
 *
 * @param objectKey {number} Index of the object which needs to be updated.
 * @param content  {string} Text content of the object which should be updated.
 * @param selectedPage  {number} Current selected page while typing text in shape
 */
export const setShapeTextContent: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(objectKey: number | string, content: string, selectedPage: number, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			let { groupSelectionIndices } = state.selection.present;
			const { selectedPageObjects } = state.selection.present;

			const page = getSelectedPage(state);
			if (!page) {
				throw "ERROR: There is no page selected to perform paste action.";
			}

			const { pageObjects } = page;

			// Object key comes through as a string if the passed object is a subgroup element.  In that
			// case, we determine the group with the first number in the string and the group selection index
			// with the second.  If there are more than 2 numbers we cannot edit more than one layer deep
			// so we exit.
			if (typeof objectKey === "string") {
				const keys = objectKey.split("_");

				if (keys.length === 2) {
					objectKey = parseInt(keys[0], 10);
					groupSelectionIndices = [parseInt(keys[1], 10)];
				} else {
					throw "ERROR: Passed object key cannot be found.";
				}
			}

			if (!pageObjects[objectKey]) {
				throw "ERROR: This action can not be performed for specified shape.";
			}

			let selectedPageObjectReference = pageObjects[objectKey];
			if (groupSelectionIndices !== undefined && groupSelectionIndices.length > 0) {
				const groupShape = selectedPageObjectReference as IGroupShape;
				selectedPageObjectReference = groupShape.childObjects[groupSelectionIndices[0]];
			}
			// selected page object must be of type shape
			if (selectedPageObjectReference.type !== IPageObjectTypes.SHAPE) {
				throw "ERROR: This action can be performed only for Shape object.";
			}
			const selectedShape = selectedPageObjectReference as IShape;

			// Shape should not be locked
			if (selectedShape.spPr.locked) {
				throw "ERROR: This action can not be performed for locked shape.";
			}
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				transient,
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const setShapeTextContentAction: ISetShapeTextContentAction = {
				transient,
				groupSelectionIndices,
				selectedPage,
				selectedPageObjects,
				objectKey,
				content,
				type: PageActionConstants.SET_TEXT_CONTENT,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, setShapeTextContentAction]));
		};

/**
 * Set source rectangle refExt  action creator
 * @param refExt Source rectangle refExt to set
 * @return Batch action consisting of selection action and setSrcRectRefExt action
 */
export const setSrcRectRefExt: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(refExt: IExtents) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (selectedPageObjects.length < 1 || !selectedPage) {
				throw "ERROR: Page and at least 1 objects should be selected to perform setSrcRectRefExt action.";
			}
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};

			return dispatch(batchActions([selectObjectsByIndexAction, {
				transient: false,
				type: PageActionConstants.SET_SRC_RECT_REF_EXT,
				selectedPageObjects,
				selectedPage,
				refExt,
			}]));
		};

/**
 * Change shape type action creator
 * @param newShapeType the new shape type
 */
export const changeShapeType: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(newShapeType: ShapeType | string) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (selectedPageObjects.length < 1 || !selectedPage) {
				throw "ERROR: Page and at least 1 objects should be selected to perform change shape type action.";
			}
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			if (newShapeType === "custom") {
				return dispatch(batchActions([selectObjectsByIndexAction, {
					transient: false,
					type: PageActionConstants.MAKE_SHAPE_CUSTOM,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					newShapeType,
				}]));
			} else {
				return dispatch(batchActions([selectObjectsByIndexAction, {
					transient: false,
					type: PageActionConstants.CHANGE_SHAPE_TYPE,
					selectedPageObjects,
					selectedPage,
					groupSelectionIndices,
					newShapeType,
				}]));
			}
		};

/**
 * Set Text style action creator
 * @param style Text style
 * @param value Text style value
 * @param isApply Flag to apply style or not
 */
export const setTextStyle: ActionCreator<ThunkAction<BatchAction | AnyAction, ICanvasEditorState, void, BatchAction | AnyAction>> =
	(style: string, value: string, isApply: boolean) =>
		(dispatch: Dispatch<BatchAction | AnyAction>, getState: () => ICanvasEditorState): BatchAction | AnyAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage, textEditorInstance } = state.selection.present;
			const { mode, placeholderTextFormat } = state.canvasEditor;
			// apply style to shape text if shape is selected or add it as default style for text
			if (selectedPageObjects.length && selectedPage) {
				const actions: AnyAction[] = [];
				actions.push({
					transient: mode === CanvasMode.EDIT_TEXT,
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				} as ISelectObjectsByIndexAction);
				if (mode !== CanvasMode.EDIT_TEXT || style === VERTICALALIGN_KEY) {
					const selectedShapes = getSelectedPageObjects(state);
					// remove the migrated extends if there any before applying text style
					if (selectedShapes) {
						const shapeIds = getMigratedShapeIds(selectedShapes);
						if (shapeIds.length) {
							actions.push({
								transient: true,
								type: PageActionConstants.REMOVE_MIGRATED_EXTENTS,
								shapeIds,
							} as IRemoveMigratedExtents);
						}
					}
					actions.push({
						isApply,
						transient: false,
						type: PageActionConstants.APPLY_TEXT_STYLE_TO_SELECTED_SHAPE,
						style,
						value,
						mode,
						groupSelectionIndices,
						selectedPage,
						selectedPageObjects,
						placeholderTextFormat,
					} as ISetShapeTextStyle);
				} else {
					setFroalaTextStyle(textEditorInstance, style, value, isApply);
				}
				return dispatch(batchActions(actions));
			} else {
				return dispatch({
					transient: true,
					type: SelectionConstants.SET_TEXT_DEFAULT_STYLES,
					style,
					value,
				});
			}
		};

/**
 * Scale image to fit in the placeholder or fill it
 * @param scaleType {string}
 * @param originalAspectRatio {Object[]}
 */
export const scaleImageToFrame: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(scaleType: string, originalAspectRatios: any[]) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}
			const scaleImageToFrameAction: IScaleImageToFrameAction = {
				scalingType: scaleType,
				transient: false,
				type: PageActionConstants.SCALE_IMAGE_TO_FRAME,
				groupSelectionIndices,
				originalAspectRatios,
				selectedPage,
				selectedPageObjects,
			};
			return dispatch(batchActions([scaleImageToFrameAction]));
		};

/**
 * Scale object extends action creator.
 * @param cxOffset {number}
 * @param cyOffset {number}
 */
export const scaleObjectExtends: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(cxOffset: number, cyOffset: number, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform scale action.";
			}
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				transient,
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const scaleObjectExtendsAction: IScaleObjectExtendsAction = {
				transient,
				type: PageActionConstants.SCALE_EXTENDS,
				groupSelectionIndices,
				selectedPage,
				selectedPageObjects,
				cxOffset,
				cyOffset,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, scaleObjectExtendsAction]));
		};

/**
 * Set object extends action creator.
 * @param cxOffset {number}
 * @param cyOffset {number}
 */
export const setObjectExtends: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(extend: { cx?: number; cy?: number }, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and at least one object should be selected to perform set extend action.";
			}
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				transient,
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const setObjectExtendsAction: ISetObjectExtendsAction = {
				transient,
				type: PageActionConstants.SET_OBJECT_EXTENDS,
				groupSelectionIndices,
				selectedPage,
				selectedPageObjects,
				extend,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, setObjectExtendsAction]));
		};

/**
 * Reset placeholder extends action creator.
 * This function is useful when actions mutate attribute placeholder objects.
 * @param transient {boolean} transient flag
 */
export const resetPlaceholderExtends: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(placeholder?: IGroupShape, transient?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;

			// if we have a placeholder selected, use that, otherwise, use placeholder object directly
			let resetPlaceholderExtendsAction: IResetPlaceholderExtendsAction;
			if (placeholder) {
				resetPlaceholderExtendsAction = {
					groupSelectionIndices,
					placeholder,
					selectedPage,
					selectedPageObjects,
					transient,
					type: PageActionConstants.RESET_PLACEHOLDER_EXTENDS,
				};
				return dispatch(batchActions([resetPlaceholderExtendsAction]));
			} else {
				// Throw error message if there is no page and object selected
				if (!selectedPageObjects.length || !selectedPage) {
					throw "ERROR: Page and at least one object should be selected to perform set extend action.";
				}
				const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
					transient,
					selectedPageObjects,
					type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				};
				resetPlaceholderExtendsAction = {
					transient,
					type: PageActionConstants.RESET_PLACEHOLDER_EXTENDS,
					groupSelectionIndices,
					selectedPage,
					selectedPageObjects,
				};
				return dispatch(batchActions([selectObjectsByIndexAction, resetPlaceholderExtendsAction]));
			}
		};

/**
 * Delete selected curve point action creator.
 */
export const deleteCurvePoint: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = () =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const newState = _.cloneDeep(state);
		const { groupSelectionIndices, selectedPageObjects, selectedPage } = newState.selection.present;
		const { selectedCurvePoint, mode } = newState.canvasEditor;
		const page = newState.pages.present[selectedPage as number - 1];
		const selectedObjects = page.pageObjects.filter(filterSelectedObjects(selectedPageObjects));
		const selectedObj = selectedObjects.filter(filterUnLockedObjects)[0];
		let pageObject: IShape | undefined;
		let groupObject;
		if (groupSelectionIndices && groupSelectionIndices.length === 1) {
			groupObject = selectedObj as IGroupShape;
			pageObject = groupObject.childObjects[groupSelectionIndices[0]] as IShape;
		} else {
			pageObject = selectedObj as IShape;
		}

		const actions: AnyAction[] = [];
		// Throw error message if there is no page and object selected
		if (!selectedPageObjects.length || !selectedPage) {
			throw "ERROR: Page and at least one object should be selected to perform this action.";
		}

		if (typeof selectedCurvePoint !== "number" || ((mode !== CanvasMode.EDIT_CURVE && mode !== CanvasMode.EDIT_PEN) &&
			mode !== CanvasMode.EDIT_PICTURE) || (pageObject.spPr.geometry.type !== GeometryTypes.CUSTOM && pageObject.spPr.geometry.type !== GeometryTypes.PEN)) {
			throw `ERROR: Selected Shape should be curve or Selected Curve Point should be number
                or mode should be EDIT_CURVE to perform this action`;
		}

		const geometry = pageObject.spPr.geometry as ICustomGeometry;
		const penGeometry = pageObject.spPr.geometry as any as IPenGeometry;
		const pathList = geometry.pathLst[0].operations;

		let isCloseCurve = false;
		const lastOperation = pathList[pathList.length - 1];
		if (lastOperation.type === PathOperationTypes.CLOSE) {
			isCloseCurve = true;
		}

		// Delete the shape completely if deleting the last point or reducing closed
		// curve below 3 points.  Closed curves have initial point -> lines -> "close"
		// command, so the pathList is of length 5 when they have 3 points remaining.
		if ((isCloseCurve && pathList.length <= 5) || (pathList.length <= 2)) {
			const selectObjectsByIndexAction: ISelectObjectsByIndexAction = {
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			const deleteObjectsAction: IDeleteObjectsAction = {
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
				type: PageActionConstants.DELETE_OBJECTS,
			};
			const deselectAllObjects: IDeselectAllObjectAction = {
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};
			const setCanvasMode: ISetCanvasModeAction = {
				mode: CanvasMode.SELECT,
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
			};
			return dispatch(batchActions([selectObjectsByIndexAction, deleteObjectsAction, deselectAllObjects, setCanvasMode]));
		}

		// if selectedCurvePoint is last point then next selected point would be (selectedCurvePoint - 1)
		if ((pathList.length - 1) === selectedCurvePoint
			|| pathList.length - 2 === selectedCurvePoint && isCloseCurve) {
			actions.push({
				pointIndex: selectedCurvePoint - 1,
				transient: true,
				type: CanvasEditorConstants.SET_CURVE_POINT_INDEX,
			} as ISetSelectedCurvePointAction);
		}

		// generate shape path list after deleting curve point
		pathList.splice(selectedCurvePoint, 1);

		// If shape is of type pen, also delete the relevant handle
		let newHandlePoints: IHandlePoint[] = undefined;
		if (penGeometry.handlePoints) {
			newHandlePoints = penGeometry.handlePoints;
			newHandlePoints.splice(selectedCurvePoint, 1);
			geometry.pathLst[0].operations = generatePenOperationList(buildShapePointList(geometry), newHandlePoints, isCloseCurve);
		} else {
			geometry.pathLst[0].operations = generateShapePathList(
				buildShapePointList(geometry), false,
				isCloseCurve, geometry.lineShape,
			);
		}

		const { shapePathLst, deltaOffX, deltaOffY, widthDiffPer, heightDiffPer, shapePathDelta } =
			getModifiedCurveDelta(pageObject as IShape | IConnectionShape);

		let isPicture = false;
		if (pageObject.type === IPageObjectTypes.SHAPE) {
			isPicture = pageObject.isPicture || false;
		}

		let cropX = 0;
		let cropY = 0;

		const shapePathOffsetX = shapePathDelta.x;
		const shapePathOffsetY = shapePathDelta.y;

		if (isPicture) {
			cropX = shapePathOffsetX || 0;
			cropY = shapePathOffsetY || 0;
		}

		actions.push({
			cropDHeight: heightDiffPer,
			cropDWidth: widthDiffPer,
			cropDX: cropX,
			cropDY: cropY,
			dHeight: heightDiffPer,
			dWidth: widthDiffPer,
			dX: deltaOffX,
			dY: deltaOffY,
			isCroppingShapeScale: isPicture,
			mode,
			selectedPageObjects,
			selectedPage,
			groupSelectionIndices,
			transient: false,
			type: PageActionConstants.SCALE,
		});

		// Select the objects by index
		actions.push({
			transient: true,
			groupSelectionIndices,
			selectedPageObjects,
			type: SelectionConstants.STORE_OBJECT_SELECTIONS,
		});

		// Set curve points action
		actions.push({
			handlePoints: newHandlePoints,
			pathLst: shapePathLst,
			transient: true,
			type: PageActionConstants.SET_CURVE_POINTS,
			selectedPageObjects,
			selectedPage,
			groupSelectionIndices,
		});
		return dispatch(batchActions(actions));
	};

/**
 * Remove migrated extents action creator
 * @param shapeIds {string[]} Array of shapeIds
 * @return {IRemoveMigratedExtents}
 */
export const removeMigratedExtents: ActionCreator<IRemoveMigratedExtents> = (shapeIds: string[]) => ({
	transient: true,
	type: PageActionConstants.REMOVE_MIGRATED_EXTENTS,
	shapeIds,
});

/**
 * Create actions to show guidelines.
 */
export const createGuidelineActions = (
	transient: boolean | undefined,
	isEscape?: boolean, isSnapToGuide?: boolean,
	guidesObj?: { x: undefined; y: undefined },
): any[] => {
	// Always call reset guide action before firing set guide action.
	const guideActions: any[] = [{
		transient: true,
		type: GuideActionConstants.RESET_GUIDES,
	}];

	// This action might change conditionally.
	const snapDetailsAction = {
		snapDetails: { snapXToGuides: false, snapYToGuides: false },
		transient: true,
		type: GuideActionConstants.SET_GUIDES_SNAP_DETAILS,
	};

	if (transient) {
		// Snap to Guide
		if (isSnapToGuide && !isEscape) {
			const guides: any = [];
			const snapDetails = { snapXToGuides: false, snapYToGuides: false };

			// Shift object so it will Snap to guides
			guidesObj = guidesObj || { x: undefined, y: undefined };

			if (guidesObj && guidesObj.hasOwnProperty("x") && guidesObj.x) {
				guides.push(guidesObj.x);
				snapDetails.snapXToGuides = true;
			}

			if (guidesObj && guidesObj.hasOwnProperty("y") && guidesObj.y) {
				guides.push(guidesObj.y);
				snapDetails.snapYToGuides = true;
			}

			// Set guide snapped details action.
			snapDetailsAction.snapDetails = snapDetails;
			guideActions.push(snapDetailsAction);

			// Set guides action object.
			const setGuideAction: ISetGuidesAction = {
				guides,
				transient: true,
				type: GuideActionConstants.SET_GUIDES,
			};

			guideActions.push(setGuideAction);
		}
	} else {
		guideActions.push(snapDetailsAction);
	}

	return guideActions;
};

/**
 * Toggle autospace mode on attribute image placeholders for all placeholders
 * @return {BatchAction}
 */
export const togglePlaceholderAutospace: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = () =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
		const action: AnyAction[] = [];
		if (selectedPageObjects.length && selectedPage) {
			// Select object by index action called to keep track of selection in undo/redo stack
			action.push({
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			});
			action.push({
				type: PageActionConstants.TOGGLE_PLACEHOLDER_AUTOSPACE,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
			});
		}
		return dispatch(batchActions(action));
	};

/**
 * Toggle visibility on labels in attribute image placeholders for all placeholders and its subelements
 * @return {BatchAction}
 */
export const togglePlaceholderLabels: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = () =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
		const action: AnyAction[] = [];
		if (selectedPageObjects.length && selectedPage) {
			// Select object by index action called to keep track of selection in undo/redo stack
			action.push({
				selectedPageObjects,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			});
			action.push({
				type: PageActionConstants.TOGGLE_PLACEHOLDER_LABELS,
				selectedPageObjects,
				selectedPage,
				groupSelectionIndices,
			});
		}
		return dispatch(batchActions(action));
	};

/**
 * Toggle placeholder labels optionally based on edit mode and value content.
 * @return {BatchAction}
 */
export const togglePlaceholderLabelsOptionally: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (canvasEditMode: boolean) =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const action: AnyAction[] = [];
		action.push({
			type: PageActionConstants.TOGGLE_PLACEHOLDER_LABELS_OPTIONALLY,
			canvasEditMode,
		});
		return dispatch(batchActions(action));
	};

/**
 * Page APIs
 * Collection of redux dispatch bound actions
 */
export const initPageAPI = (dispatch: Dispatch<any>) => {
	return bindActionCreators({
		addImage,
		addImages,
		addImageToPlaceholder,
		addPage,
		addPageObject,
		addPlaceholders,
		setShapeAdjustValues,
		setShapeAutosizeValues,
		sortPages,
		alignObjects,
		applyFormatPainter,
		changeShapeType,
		changePageObjectOrder,
		removeImpossiblePaths,
		deleteObjects,
		deletePage,
		deleteShapeById,
		deleteCurvePoint,
		distributeObjects,
		fillPlaceholderWithAttributes,
		flipObjects,
		group,
		lockObjects,
		moveObjects,
		paste,
		removeMigratedExtents,
		resetPlaceholderExtends,
		rotateObjects,
		scaleImageToFrame,
		scaleObjects,
		scaleObjectExtends,
		setObjectExtends,
		setCurvePoints,
		setHandlePoints,
		setLineCap,
		setLineColor,
		setLineDash,
		setLineType,
		setObjectsFill,
		setObjectsShape,
		setOffsets,
		setPageMeta,
		setPages,
		setShapeGeometry,
		setShapeTextContent,
		setSrcRectRefExt,
		setStrokeWidth,
		setTextStyle,
		translateObjects,
		togglePlaceholderAutospace,
		togglePlaceholderLabels,
		togglePlaceholderLabelsOptionally,
		ungroup,
	}, dispatch);
};
